/**
 * sourceOpener.tsx — opening a cited source PDF, in ONE place.
 *
 * `KBR-74`, 2026-09-14. «Esplora KB» has had working citation links since
 * `KBR-68`; «Assistente CME» needed the same behaviour. The logic looks small
 * and is not: three independent review lenses found a defect in the first
 * version of it, and the fix (open the tab inside the click gesture, before any
 * await) is invisible unless you know why it is there. Copying it into a second
 * screen would copy the shape and lose the reason — so both screens call this.
 *
 * What it does NOT do: decide who may read what. The browser hands a document id
 * back to the Knowledge Base with the reader's own token, and
 * `GET /v1/documents/{id}/source-url` applies `KBR-27` (1) there. A refusal is
 * SHOWN rather than swallowed — silently doing nothing is how a correct
 * permission rule gets reported as a dead button.
 */
import { useCallback, useState } from "react";

import { ApiError } from "./api";
import { useKbDocumentSource } from "./kbApi";
import { useLineItemBillPage } from "./tenderApi";

/** «pagina PDF 60» / «pagine PDF 60-61» / «». The PDF SHEET index, never the
 *  number printed on the sheet — `KBR-68`. The two services spell this the same
 *  way by convention; this is the browser's copy of that convention. */
export function pdfPageLabel(pageStart?: number | null, pageEnd?: number | null): string {
  if (!pageStart) return "";
  return pageEnd && pageEnd !== pageStart
    ? `pagine PDF ${pageStart}-${pageEnd}`
    : `pagina PDF ${pageStart}`;
}

/** The one Italian sentence for a window the browser refused to open. */
export const POPUP_BLOCKED_TEXT =
  "Il browser ha bloccato l'apertura del documento. Consenti le finestre popup per questo sito e riprova.";

/**
 * Open an empty tab for a document whose URL is not known yet, or null when the browser
 * refuses. Call it SYNCHRONOUSLY inside the click handler, before any `await`.
 *
 * G3a moved this out of `useSourceOpener` unchanged, so the bill page of an Audit item opens
 * through the same three lines and keeps the reason they are written this way:
 */
export function openBlankTab(): Window | null {
  // The tab is opened SYNCHRONOUSLY, inside the click gesture. `window.open`
  // after an `await` is a programmatic open with no user gesture behind it,
  // which Safari and any blocked-popup profile refuse — returning null,
  // which the first version discarded, so the chip was a dead button with no
  // message anywhere.
  //
  // **`FE-1`, and the feature that reads as safety is the defect.** The
  // third argument used to be the two isolation flags. The HTML Standard's
  // window-open steps require a **null return** for `noopener` with
  // `_blank` — the whole point of `noopener` is that the caller gets no
  // handle — and `noreferrer` implies it. So this branch was taken on the
  // SUCCESS path, on a standards-compliant browser, every time: the function
  // returned before it ever asked for the signed URL, leaving the reader a
  // blank tab and a message telling them to enable popups, which cannot help
  // because nothing was blocked. Every citation chip on both «Assistente
  // CME» and «Esplora KB» went through here.
  //
  // Opened without those flags the call returns a real handle, so null now
  // means what the message says. Isolation is kept by severing the link
  // ourselves, before anything is put in the tab — that is what those flags
  // exist to achieve, and it is the half that matters, because it stops the
  // opened page from navigating ours. The half given up is the referrer, so
  // the storage host may see this app's URL; it already does on every fetch.
  const tab = window.open("", "_blank");
  if (!tab) return null;
  try {
    tab.opener = null;
  } catch {
    // A browser that refuses the assignment keeps its own opener policy; the
    // tab still opens and still navigates, which is the reader's need.
  }
  return tab;
}

export type OpenableSource = {
  documentId?: string | null;
  documentTitle?: string | null;
  title?: string | null;
  pageStart?: number | null;
};

export type SourceOpenError = { key: string; text: string };

export function useSourceOpener() {
  const fetchDocumentSource = useKbDocumentSource();
  const [error, setError] = useState<SourceOpenError | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const open = useCallback(
    async (source: OpenableSource, key: string) => {
      setError(null);
      if (!source.documentId || busy) return;
      // The tab is opened SYNCHRONOUSLY, inside the click gesture — see `openBlankTab`.
      const tab = openBlankTab();
      if (!tab) {
        setError({ key, text: POPUP_BLOCKED_TEXT });
        return;
      }
      setBusy(key);
      try {
        const src = await fetchDocumentSource(source.documentId);
        tab.location.href = `${src.url}${source.pageStart ? `#page=${source.pageStart}` : ""}`;
      } catch (err) {
        tab.close();
        // Italian, and about the DOCUMENT — never the server's English detail
        // with a raw UUID in it, which is what the first version printed into an
        // otherwise Italian panel.
        const name = source.documentTitle || source.title || "il documento";
        setError({
          key,
          text:
            err instanceof ApiError && err.status === 403
              ? `«${name}» non è consultabile in questo momento: è in lavorazione o archiviato. Riprova più tardi o chiedi a un amministratore.`
              : err instanceof ApiError && err.status === 404
                ? `«${name}» non è più disponibile: potrebbe essere stato eliminato.`
                : `Non è stato possibile aprire «${name}».`,
        });
      } finally {
        setBusy(null);
      }
    },
    [fetchDocumentSource, busy],
  );

  return { open, error, busy, clearError: () => setError(null) };
}

/**
 * G3a — open the stored bill (the CME PDF the analysis read) at the page an Audit item was
 * read from, in a new tab. Same order as `useSourceOpener`: the tab first, inside the click;
 * then the signed URL from `GET /v1/line-items/{id}/bill-page`; then `#page=<first page>`.
 *
 * The page comes from the server's answer, which is read at the moment of the click; the page
 * the row shows is the fallback. A refusal is SHOWN, in Italian, and the empty tab is closed.
 */
export function useBillPageOpener() {
  const fetchBillPage = useLineItemBillPage();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const open = useCallback(
    async (lineItemId: string, pageStart: number) => {
      setError(null);
      if (busy) return;
      const tab = openBlankTab();
      if (!tab) {
        setError(POPUP_BLOCKED_TEXT);
        return;
      }
      setBusy(true);
      try {
        const src = await fetchBillPage(lineItemId);
        tab.location.href = `${src.url}#page=${src.pageStart ?? pageStart}`;
      } catch (err) {
        tab.close();
        // 404: the item is not in the reader's company, its page was never recorded, or the
        // stored bill is gone. To the reader all three are the same fact.
        setError(
          err instanceof ApiError && err.status === 404
            ? "Il computo di questa voce non è più disponibile: potrebbe essere stato eliminato."
            : "Non è stato possibile aprire il computo. Riprova.",
        );
      } finally {
        setBusy(false);
      }
    },
    [fetchBillPage, busy],
  );

  return { open, error, busy };
}
